import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface Section {
  heading: string;
  content: string;
}

interface RelatedLink {
  label: string;
  to: string;
}

interface ArticleData {
  title: string;
  subtitle: string;
  metaDescription?: string;
  intro: string;
  competitorName?: string;
  sections: Section[];
  verdict?: string;
  relatedLinks: RelatedLink[];
}

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://hipobuyspread.skin';

export default function ArticlePage({ data }: { data: ArticleData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageUrl = `${SITE_URL}${location.pathname}`;

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <nav className="max-w-4xl mx-auto px-4 md:px-8 py-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 font-body">
            <li>
              <a href="/" onClick={handleHomeClick} className="hover:text-primary-600 transition-colors">Home</a>
            </li>
            <li className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li className="text-foreground-700" aria-current="page">{data.title}</li>
          </ol>
        </nav>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 md:px-8 pb-16">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
              {data.title}
            </h1>
            <p className="text-lg md:text-xl text-foreground-600 font-body">
              {data.subtitle}
            </p>
          </header>

          {/* Intro */}
          <div className="prose prose-lg max-w-none mb-12">
            {data.intro.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-foreground-700 font-body leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>

          {/* CTA Banner */}
          <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6 md:p-8 mb-12">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-1">
                  Start Shopping with Hipobuy Spreadsheet
                </h3>
                <p className="text-sm text-foreground-600 font-body">
                  Browse organized product categories on kaspreadsheet.com
                </p>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold font-label hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>

          {/* Sections */}
          {data.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                {section.heading}
              </h2>
              <div className="prose max-w-none">
                {section.content.split('\n\n').map((p, pIdx) => (
                  <p key={pIdx} className="text-foreground-700 font-body leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}

          {/* Verdict */}
          {data.verdict && (
            <section className="mb-12 bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                Final Recommendation
              </h2>
              <p className="text-foreground-700 font-body leading-relaxed">
                {data.verdict}
              </p>
            </section>
          )}

          {/* Internal Links */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
              Related Articles
            </h2>
            <div className="flex flex-wrap gap-3">
              {data.relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 border border-background-200 rounded-full text-sm font-medium text-foreground-800 font-label hover:bg-background-200 transition-colors cursor-pointer"
                >
                  {link.label}
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              ))}
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-100 border border-primary-200 rounded-full text-sm font-medium text-primary-700 font-label hover:bg-primary-200 transition-colors cursor-pointer"
              >
                Back to Home
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-home-line text-xs"></i>
                </span>
              </Link>
            </div>
          </section>

          {/* Article Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: data.title,
                description: data.metaDescription || data.subtitle,
                author: { '@type': 'Organization', name: 'Hipobuy Spreadsheet Guide' },
                publisher: { '@type': 'Organization', name: 'Hipobuy Spreadsheet Guide' },
                datePublished: '2026-01-09',
                dateModified: '2026-07-09',
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': pageUrl,
                },
              }),
            }}
          />

          {/* Breadcrumb Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: data.title,
                    item: pageUrl,
                  },
                ],
              }),
            }}
          />
        </article>
      </main>

      <Footer />
    </div>
  );
}